import { ServiceBooking } from "@turizi/shared";
import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Button, NumberInput, Popover, Switch, Loader } from "@mantine/core";
import { MoneyIn } from "../snippets";
import { formatPrice, formatExchangeRate } from "@/lib/format-utils";
import CurrencySelector from "../currency-selector";
import { IconCashEdit, IconHandClick, IconArrowUpRight, IconArrowDownRight, IconExchange, IconRefreshAlert } from "@tabler/icons-react";
import { useResetServiceBookingCost, useUpdateServiceBookingModifiers } from "@/hooks/api-hooks";
import { localUser } from "@/services/session-service";

interface CostSectionProps {
    booking: ServiceBooking | undefined;
    save: (data: Partial<ServiceBooking>) => void;
    updating: boolean;
    canEditManualPriceCost?: boolean;
}

export default function CostSection({
    booking,
    save,
    updating,
    canEditManualPriceCost,
}: CostSectionProps) {
    const { t } = useTranslation();
    const currentUser = localUser();
    const canEdit = canEditManualPriceCost !== undefined
        ? canEditManualPriceCost
        : currentUser?.permissions?.edit_prices_and_costs_manually !== false;
    const {
        mutate: updateModifiers,
        isPending: updatingModifiers
    } = useUpdateServiceBookingModifiers();
    const {
        mutate: resetCost,
        isPending: resettingCost
    } = useResetServiceBookingCost();
    const [manualCost, setManualCost] = useState<number | null>(null);
    const [manualCurrency, setManualCurrency] = useState<string | null>(booking?.money_out_currency || null);
    const [costPopoverOpened, setCostPopoverOpened] = useState(false);

    const availableModifiers = useMemo(() => {
        const appliedMods = booking?.applied_money_out_mods || [];
        const templateMods = booking?.service_data?.amount_out_modifiers || [];

        const map = new Map<string, { id: string; name: string; amount: number; applied: boolean }>();
        for (const tm of templateMods) {
            map.set(tm.id, { id: tm.id, name: tm.name, amount: tm.amount, applied: false });
        }
        for (const am of appliedMods) {
            map.set(am.id, { id: am.id, name: am.name, amount: am.amount, applied: !!am.applied });
        }
        return Array.from(map.values());
    }, [booking?.applied_money_out_mods, booking?.service_data?.amount_out_modifiers]);

    const handleToggleModifier = (modId: string, currentApplied: boolean) => {
        if (!booking?.id) return;
        const newApplied = !currentApplied;
        const updatedMods = availableModifiers.map(m =>
            m.id === modId ? { ...m, applied: newApplied } : m
        );

        updateModifiers({
            id: booking.id,
            applied_money_out_mods: updatedMods,
        });
    };

    const handleReset = () => {
        if (!booking?.id) return;
        resetCost({ id: booking.id }, {
            onSuccess: () => {
                setCostPopoverOpened(false);
            }
        });
    };

    const origAmount = booking?.cost_info?.money_out_amount;
    const origCurrency = booking?.cost_info?.money_out_currency || '**';
    const currentAmount = booking?.money_out_amount;
    const currentCurrency = booking?.money_out_currency || '**';

    const isManual = origAmount !== currentAmount || origCurrency !== currentCurrency;

    const isSameCurrency = !origCurrency || !currentCurrency || origCurrency === currentCurrency;
    const isDifferentCurrency = Boolean(origCurrency && currentCurrency && origCurrency !== currentCurrency);

    let percentDiff: number | null = null;
    let exchangeRate: number | null = null;

    if (isManual && origAmount != null && currentAmount != null && origAmount > 0) {
        if (isSameCurrency) {
            percentDiff = ((currentAmount - origAmount) / origAmount) * 100;
        } else if (isDifferentCurrency) {
            exchangeRate = currentAmount / origAmount;
        }
    }

    useEffect(() => {
        if (!costPopoverOpened) {
            setManualCost(null);
        }
    }, [costPopoverOpened]);

    if (!booking?.cost_info && booking?.money_out_amount == null && availableModifiers.length === 0) {
        return null;
    }

    return (
        <div className="solid-box-border-tinted space-y-2">
            {/* Cost Modifiers */}
            {availableModifiers.length > 0 && (
                <div className="space-y-1.5 pb-2 border-b border-gray-200">
                    <div className="space-y-1">
                        {availableModifiers.map((mod) => {
                            const isAddition = mod.amount >= 0;
                            return (
                                <div
                                    key={mod.id}
                                    className="flex items-center justify-between gap-2 p-1.5 rounded-md bg-white border border-gray-100 hover:border-gray-200 transition-colors"
                                >
                                    <div className="flex items-center gap-2 min-w-0">
                                        {
                                            updatingModifiers
                                                ?
                                                <Loader size="xs" color="black" />
                                                :
                                                <Switch
                                                    size="xs"
                                                    color="black"
                                                    checked={mod.applied}
                                                    disabled={updatingModifiers || Boolean(booking?.parent_booking_id)}
                                                    onChange={() => handleToggleModifier(mod.id, mod.applied)}
                                                />
                                        }
                                        <span className="text-xs text-gray-700 truncate font-medium">
                                            {mod.name}
                                        </span>
                                    </div>
                                    <span className={`text-xs font-bold shrink-0 ${isAddition ? 'text-green-700' : 'text-red-700'}`}>
                                        {isAddition ? '+' : ''}{formatPrice(mod.amount)} <span className="text-gray-400 font-normal">{booking?.money_out_currency || booking?.cost_info?.money_out_currency}</span>
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {!booking?.parent_booking_id && canEdit && (
                <Button
                    variant="subtle"
                    color="green"
                    radius="xl"
                    size="xs"
                    onClick={handleReset}
                    loading={resettingCost}
                    disabled={updating || resettingCost}
                    leftSection={<IconRefreshAlert size={16} stroke={2} />}
                    fullWidth
                >
                    {t('reset_cost')}
                </Button>
            )}
            <div
                className="max-h-[300px] overflow-y-auto space-y-2"
                style={{
                    maskImage: 'linear-gradient(to bottom, black 0%, black 85%, transparent 100%)',
                    WebkitMaskImage: 'linear-gradient(to bottom, black 0%, black 85%, transparent 100%)'
                }}>
                {/* Cost Breakdown Details */}
                {booking?.cost_info?.steps?.map((step: any, index: number) => {
                    return (
                        <div key={step.id || `step-${index}`} className="space-y-0.5">
                            {/* Step Header */}
                            <div className="text-xs font-bold text-gray-800 uppercase tracking-wide flex items-center justify-between">
                                <div>
                                    {t(step.label)}
                                </div>
                            </div>
                            {/* Sub-steps */}
                            <div>
                                {step.subSteps?.map((subStep: any, subIndex: number) => (
                                    <div key={subStep.id || `substep-${index}-${subIndex}`} className="flex items-start justify-between gap-3 py-1">
                                        <div className="flex-1 min-w-0">
                                            <div className="text-xs text-gray-700">
                                                {subStep?.label}
                                            </div>
                                            <div className="text-xxs text-gray-400 ">
                                                {subStep?.calculation}
                                            </div>
                                        </div>
                                        <div className="text-xs font-semibold text-gray-900 whitespace-nowrap">
                                            {subStep?.negative ? '-' : ''}{formatPrice(subStep?.amount)} <span className="text-gray-400 font-normal">{booking?.cost_info?.money_out_currency || booking?.money_out_currency}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                }) || null}
            </div>

            {/* Total Cost */}
            {!canEdit ? (
                <div className="w-full solid-box-border flex items-center gap-6 justify-between select-none">
                    <div />
                    <div className="flex gap-1 items-center justify-end">
                        <MoneyIn
                            className="text-lg font-bold text-red-500"
                            amount={booking?.money_out_amount}
                            currency={booking?.money_out_currency}
                        />
                        {isManual && <IconHandClick size={12} color="gray" />}
                    </div>
                </div>
            ) : (
                <Popover
                    withArrow
                    shadow="xl"
                    radius="md"
                    trapFocus
                    opened={costPopoverOpened}
                >
                    <Popover.Target>
                        <div
                            className="w-full solid-box-border-hover flex items-center gap-6 justify-between cursor-pointer group"
                            onClick={() => setCostPopoverOpened(true)}>
                            <div className="flex items-center gap-1 text-xs text-gray-400 group-hover:text-blue-600 transition-colors">
                                <IconCashEdit size={14} />
                            </div>
                            <div className="flex gap-1 items-center justify-end">
                                <MoneyIn
                                    className="text-lg font-bold text-red-500"
                                    amount={booking?.money_out_amount}
                                    currency={booking?.money_out_currency}
                                />
                                {isManual && <IconHandClick size={12} color="gray" />}
                            </div>
                        </div>
                    </Popover.Target>
                <Popover.Dropdown>
                    <div className="space-y-1 p-1">
                        <div className="text-sm font-semibold text-gray-900">
                            {t('update_cost_manually')}
                        </div>
                        <NumberInput
                            decimalSeparator="," // TODO internationalization
                            thousandSeparator="." // TODO internationalization
                            autoFocus
                            hideControls
                            value={manualCost}
                            onChange={(value) => setManualCost(Number(value))}
                            size="md"
                        />
                        <CurrencySelector
                            value={manualCurrency}
                            onChange={(value) => setManualCurrency(value)}
                        />
                        <div className="flex justify-between gap-2 mt-4">
                            <Button
                                variant="subtle"
                                color="gray"
                                radius="xl"
                                size="xs"
                                onClick={() => {
                                    setCostPopoverOpened(false);
                                    setManualCost(null);
                                }}
                            >
                                {t('cancel')}
                            </Button>
                            <Button
                                color="dark"
                                size="xs"
                                radius="xl"
                                loading={updating}
                                disabled={manualCost === null || manualCost === booking?.money_out_amount || !manualCurrency}
                                onClick={() => {
                                    if (manualCost !== null) {
                                        save({
                                            money_out_amount: manualCost,
                                            money_out_currency: manualCurrency,
                                        });
                                        setCostPopoverOpened(false);
                                        setManualCost(null);
                                    }
                                }}
                            >
                                {t('save')}
                            </Button>
                        </div>
                    </div>
                </Popover.Dropdown>
            </Popover>
            )}

            {/* Original Cost & Adjustment Info (if manual / modified) */}
            {isManual && origAmount != null && (
                <div className="space-y-1">
                    <div className="flex justify-end items-center gap-1.5 w-full">
                        <span className="text-xs font-medium text-slate-400 whitespace-nowrap">
                            {t('original_cost')}:
                        </span>
                        <MoneyIn
                            className="text-xs font-bold text-slate-700"
                            amount={origAmount}
                            currency={origCurrency}
                        />
                    </div>

                    {/* Percentage up/down if same currency */}
                    {isSameCurrency && percentDiff != null && (
                        <div
                            className={`inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-xs font-bold whitespace-nowrap border ${percentDiff > 0
                                ? 'bg-amber-50 text-amber-800 border-amber-200'
                                : percentDiff < 0
                                    ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                                    : 'bg-slate-100 text-slate-700 border-slate-200'
                                }`}
                        >
                            {percentDiff > 0 ? (
                                <IconArrowUpRight size={14} />
                            ) : percentDiff < 0 ? (
                                <IconArrowDownRight size={14} />
                            ) : null}
                            <span>
                                {percentDiff > 0
                                    ? `+${percentDiff.toFixed(1).replace(/\.0$/, '')}%`
                                    : `${percentDiff.toFixed(1).replace(/\.0$/, '')}%`}
                            </span>
                        </div>
                    )}

                    {/* Exchange rate if different currency */}
                    {isDifferentCurrency && exchangeRate != null && (
                        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-blue-50/80 text-blue-900 border border-blue-200/80 text-xs font-semibold whitespace-nowrap">
                            <IconExchange size={14} className="text-blue-600 shrink-0" />
                            <span>
                                1 {origCurrency} = {formatExchangeRate(exchangeRate)} {currentCurrency}
                            </span>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}